import React, { useState, useEffect, useRef } from 'react';
import { PedidosService } from '../services/pedidosService';
import './GestionAlertas.css';

const GestionAlertas = () => {
  const [pedidos, setPedidos] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const notificacionMostradaRef = useRef(false);

  useEffect(() => {
    const unsubscribePedidos = PedidosService.suscribirsePedidos(setPedidos);

    return () => {
      unsubscribePedidos();
    };
  }, []);

  useEffect(() => {
    calcularAlertas();
  }, [pedidos]);

  useEffect(() => {
    // Mostrar notificación cuando hay alertas
    if (alertas.length > 0 && !notificacionMostradaRef.current) {
      notificacionMostradaRef.current = true;
      
      // Notificación del navegador
      if ('Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification('⚠️ Pedidos Retrasados', { 
            body: `Tienes ${alertas.length} pedido(s) sin cambios de estado en más de 24 horas.`,
            icon: '/logo192.png'
          });
        } else if (Notification.permission !== 'denied') {
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') {
              new Notification('⚠️ Pedidos Retrasados', { 
                body: `Tienes ${alertas.length} pedido(s) sin cambios de estado en más de 24 horas.`,
                icon: '/logo192.png'
              });
            }
          });
        }
      }
    } else if (alertas.length === 0) {
      notificacionMostradaRef.current = false;
    }
  }, [alertas]);

  const calcularAlertas = () => {
    const ahora = new Date();
    const hace24Horas = ahora.getTime() - (24 * 60 * 60 * 1000);
    
    const pedidosConAlerta = pedidos
      .filter(pedido => {
        // Excluir pedidos que ya están entregados
        if (pedido.estatus === 'entregado') {
          return false;
        }

        // Obtener la última fecha de actualización
        const fechaActualizacion = pedido.fechaActualizacion 
          ? new Date(pedido.fechaActualizacion).getTime()
          : pedido.timestamp 
            ? pedido.timestamp
            : pedido.fechaCompra 
              ? new Date(pedido.fechaCompra).getTime()
              : null;

        if (!fechaActualizacion) return false;

        // Verificar si han pasado más de 24 horas desde la última actualización
        const horasSinActualizar = (ahora.getTime() - fechaActualizacion) / (1000 * 60 * 60);
        
        return horasSinActualizar >= 24;
      })
      .map(pedido => {
        const fechaActualizacion = pedido.fechaActualizacion 
          ? new Date(pedido.fechaActualizacion).getTime()
          : pedido.timestamp 
            ? pedido.timestamp
            : new Date(pedido.fechaCompra).getTime();

        const horasSinActualizar = (ahora.getTime() - fechaActualizacion) / (1000 * 60 * 60);
        const diasSinActualizar = Math.floor(horasSinActualizar / 24);
        const horasRestantes = Math.floor(horasSinActualizar % 24);

        return {
          ...pedido,
          horasSinActualizar: Math.floor(horasSinActualizar),
          diasSinActualizar,
          horasRestantes,
          fechaUltimaActualizacion: pedido.fechaActualizacion || pedido.fechaCompra
        };
      })
      .sort((a, b) => b.horasSinActualizar - a.horasSinActualizar);

    setAlertas(pedidosConAlerta);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (estatus) => {
    const statusMap = {
      'en_preparacion': 'En Preparación',
      'ruta': 'En Ruta',
      'entregado': 'Entregado',
      'cancelado': 'Cancelado',
      'pendiente': 'Pendiente'
    };
    return statusMap[estatus] || estatus;
  };

  const getStatusClass = (estatus) => {
    switch (estatus) {
      case 'entregado':
        return 'status-entregado';
      case 'en_preparacion':
        return 'status-preparacion';
      case 'ruta':
        return 'status-ruta';
      case 'cancelado':
        return 'status-cancelado';
      case 'pendiente':
        return 'status-pendiente';
      default:
        return 'status-default';
    }
  };

  const getNivelAlerta = (horasSinActualizar) => {
    if (horasSinActualizar >= 72) return { nivel: 'crítico', color: '#dc3545' };
    if (horasSinActualizar >= 48) return { nivel: 'riesgo', color: '#fd7e14' };
    return { nivel: 'advertencia', color: '#ffc107' };
  };

  return (
    <div className="gestion-alertas">
      <div className="header-section">
        <h1>Gestión de Alertas</h1>
      </div>

      {/* Banner de Alerta */}
      {alertas.length > 0 && (
        <div className="alerta-banner">
          <div className="alerta-banner-content">
            <div className="alerta-banner-icon">⚠️</div>
            <div className="alerta-banner-text">
              <strong>¡Atención!</strong> Tienes <strong>{alertas.length}</strong> pedido(s) retrasado(s) sin cambios de estado en más de 24 horas.
            </div>
          </div>
        </div>
      )}

      {/* Estadísticas */}
      <div className="estadisticas-alertas">
        <div className="stat-card total">
          <div className="stat-info">
            <div className="stat-number">{alertas.length}</div>
            <div className="stat-label">Total de Alertas</div>
          </div>
        </div>
        <div className="stat-card critico">
          <div className="stat-info">
            <div className="stat-number">
              {alertas.filter(a => a.horasSinActualizar >= 72).length}
            </div>
            <div className="stat-label">Críticas (≥72h)</div>
          </div>
        </div>
        <div className="stat-card riesgo">
          <div className="stat-info">
            <div className="stat-number">
              {alertas.filter(a => a.horasSinActualizar >= 48 && a.horasSinActualizar < 72).length}
            </div>
            <div className="stat-label">Riesgo (48-72h)</div>
          </div>
        </div>
        <div className="stat-card advertencia">
          <div className="stat-info">
            <div className="stat-number">
              {alertas.filter(a => a.horasSinActualizar >= 24 && a.horasSinActualizar < 48).length}
            </div>
            <div className="stat-label">Advertencia (24-48h)</div>
          </div>
        </div>
      </div>

      {/* Lista de Alertas */}
      <div className="alertas-container">
        <h2>Pedidos sin cambios de estado en 24 horas ({alertas.length})</h2>
        
        {alertas.length === 0 ? (
          <div className="no-alertas">
            <div className="no-alertas-icon">✓</div>
            <p>No hay pedidos con alertas. Todos los pedidos han sido actualizados recientemente.</p>
          </div>
        ) : (
          <div className="alertas-grid">
            {alertas.map((alerta) => {
              const nivelInfo = getNivelAlerta(alerta.horasSinActualizar);
              return (
                <div 
                  key={alerta.id} 
                  className="alerta-card"
                  style={{ borderLeftColor: nivelInfo.color }}
                >
                  <div className="alerta-content">
                    <div className="alerta-header">
                      <div className="alerta-nivel">
                        Pedido: {alerta.numeroDocumento || alerta.id}
                      </div>
                      <span 
                        className="badge-bloqueado"
                        style={{ backgroundColor: nivelInfo.color }}
                      >
                        {nivelInfo.nivel.toUpperCase()}
                      </span>
                    </div>
                    <div className="alerta-body">
                      <div className="alerta-item">
                        <div>
                          <div className="alerta-item-label">Cliente</div>
                          <div className="alerta-item-value">{alerta.cliente || 'N/A'}</div>
                        </div>
                      </div>
                      <div className="alerta-item">
                        <div>
                          <div className="alerta-item-label">Estado Actual</div>
                          <span className={`status-badge ${getStatusClass(alerta.estatus)}`}>
                            {getStatusText(alerta.estatus)}
                          </span>
                        </div>
                      </div>
                      <div className="alerta-item">
                        <div>
                          <div className="alerta-item-label">Última Actualización</div>
                          <div className="alerta-item-value">
                            {formatDate(alerta.fechaUltimaActualizacion)}
                          </div>
                        </div>
                      </div>
                      <div className="alerta-item">
                        <div>
                          <div className="alerta-item-label">Tiempo sin actualizar</div>
                          <div className="alerta-item-value horas">
                            {alerta.diasSinActualizar > 0 
                              ? `${alerta.diasSinActualizar} día(s) y ${alerta.horasRestantes} hora(s)`
                              : `${alerta.horasSinActualizar} hora(s)`
                            }
                          </div>
                        </div>
                      </div>
                      {alerta.chofer && (
                        <div className="alerta-item">
                          <div>
                            <div className="alerta-item-label">Chofer</div>
                            <div className="alerta-item-value">{alerta.chofer}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GestionAlertas;
